import type { ReactNode } from "react"

const SAFE_LINK = /^(https?:\/\/|mailto:)/i

// Slack applies *bold*, _italic_ and ~strike~ only at word edges, so
// snake_case and 2*3*4 stay plain.
const INLINE =
	/`([^`\n]+)`|<([^<>\n]+)>|(?<![\w*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\w*])|(?<![\w_])_(?!\s)([^_\n]+?)(?<!\s)_(?![\w_])|(?<![\w~])~(?!\s)([^~\n]+?)(?<!\s)~(?![\w~])/g

function decodeEntities(text: string) {
	return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&")
}

function renderAngle(content: string, key: string): ReactNode {
	const [target = "", label] = content.split("|", 2)
	if (target.startsWith("@")) {
		return (
			<span
				key={key}
				className="rounded-[3px] bg-[#1D9BD1]/10 px-0.5 text-[#1264A3] dark:text-[#1D9BD1]"
			>
				@{label ?? "user"}
			</span>
		)
	}
	if (target.startsWith("#")) {
		return (
			<span
				key={key}
				className="rounded-[3px] bg-[#1D9BD1]/10 px-0.5 text-[#1264A3] dark:text-[#1D9BD1]"
			>
				#{label ?? "channel"}
			</span>
		)
	}
	if (target.startsWith("!")) {
		return (
			<span key={key} className="rounded-[3px] bg-[#E8912D]/20 px-0.5">
				@{target.slice(1).split("^")[0]}
			</span>
		)
	}
	if (!SAFE_LINK.test(target)) return decodeEntities(`<${content}>`)
	return (
		<a
			key={key}
			href={target}
			target="_blank"
			rel="noreferrer noopener"
			className="text-[#1264A3] hover:underline dark:text-[#1D9BD1]"
		>
			{decodeEntities(label ?? target)}
		</a>
	)
}

export function renderSlackInline(text: string, keyPrefix = "i"): ReactNode[] {
	const nodes: ReactNode[] = []
	let lastIndex = 0
	let index = 0

	for (const match of text.matchAll(INLINE)) {
		const start = match.index
		if (start > lastIndex)
			nodes.push(decodeEntities(text.slice(lastIndex, start)))
		const key = `${keyPrefix}-${index++}`
		const [, code, angle, bold, italic, strike] = match

		if (code !== undefined) {
			nodes.push(
				<code
					key={key}
					className="rounded-[3px] border border-[#1D1C1D]/[0.13] bg-[#1D1C1D]/[0.04] px-[3px] font-mono text-[12px] text-[#C01343] dark:border-[#E8E8E8]/[0.13] dark:bg-[#E8E8E8]/[0.04] dark:text-[#E8912D]"
				>
					{decodeEntities(code)}
				</code>,
			)
		} else if (angle !== undefined) {
			nodes.push(renderAngle(angle, key))
		} else if (bold !== undefined) {
			nodes.push(<strong key={key}>{renderSlackInline(bold, key)}</strong>)
		} else if (italic !== undefined) {
			nodes.push(<em key={key}>{renderSlackInline(italic, key)}</em>)
		} else if (strike !== undefined) {
			nodes.push(<s key={key}>{renderSlackInline(strike, key)}</s>)
		}
		lastIndex = start + match[0].length
	}

	if (lastIndex < text.length) nodes.push(decodeEntities(text.slice(lastIndex)))
	return nodes
}

function renderLines(lines: string[], keyPrefix: string): ReactNode[] {
	return lines.flatMap((line, i) => [
		...(i > 0 ? [<br key={`${keyPrefix}-br-${i}`} />] : []),
		...renderSlackInline(line, `${keyPrefix}-${i}`),
	])
}

interface LineGroup {
	quote: boolean
	lines: string[]
}

// Slack turns a blank line into a short paragraph gap, not a full empty line.
function paragraphsOf(text: string): LineGroup[][] {
	const paragraphs: LineGroup[][] = []
	let current: LineGroup[] = []
	for (const line of text.replace(/^\n+|\n+$/g, "").split("\n")) {
		if (!line.trim()) {
			if (current.length) paragraphs.push(current)
			current = []
			continue
		}
		const quote = line.startsWith("> ") || line === ">"
		const content = quote ? line.replace(/^> ?/, "") : line
		const last = current.at(-1)
		if (last && last.quote === quote) last.lines.push(content)
		else current.push({ quote, lines: [content] })
	}
	if (current.length) paragraphs.push(current)
	return paragraphs
}

/** Renders the mrkdwn of one Slack section the way Slack lays it out. */
export function SlackMrkdwn({ text }: { text: string }) {
	const parts = text.split("```")
	return (
		<div className="flex flex-col gap-2">
			{parts.flatMap((part, partIndex) => {
				if (partIndex % 2 === 1) {
					return [
						<pre
							key={`code-${partIndex}`}
							className="whitespace-pre-wrap rounded border border-[#1D1C1D]/[0.13] bg-[#1D1C1D]/[0.04] p-2 font-mono text-[12px] leading-[1.5] dark:border-[#E8E8E8]/[0.13] dark:bg-[#E8E8E8]/[0.04]"
						>
							{decodeEntities(part.replace(/^\n/, "").replace(/\n$/, ""))}
						</pre>,
					]
				}

				return paragraphsOf(part).map((groups, paragraphIndex) => (
					<div key={`p-${partIndex}-${paragraphIndex}`}>
						{groups.map((group, groupIndex) => {
							const key = `p-${partIndex}-${paragraphIndex}-${groupIndex}`
							return group.quote ? (
								<blockquote
									key={key}
									className="border-l-4 border-[#DDDDDD] pl-3 dark:border-[#E8E8E8]/30"
								>
									{renderLines(group.lines, key)}
								</blockquote>
							) : (
								<div key={key}>{renderLines(group.lines, key)}</div>
							)
						})}
					</div>
				))
			})}
		</div>
	)
}
